import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AWS from 'aws-sdk';
import Layout from '../components/Layout';
import '../styles/prescriptive-ai.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const PrescriptiveAI = ({ user }) => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    useCase: '',
    businessArea: 'pricing'
  });
  const [submitStatus, setSubmitStatus] = useState('');
  
  const isAuthenticated = user !== null;

  if (!isAuthenticated) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in to access prescriptive AI services.</p>
        </div>
      </Layout>
    );
  }

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactInfo.name || !contactInfo.email || !contactInfo.useCase) {
      setSubmitStatus('Please provide your name, email address, and use case.');
      return;
    }
    
    setSubmitStatus('Submitting...');
    
    try {
      // Get AWS credentials from Amplify Auth
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      // Configure AWS SDK with proper credentials
      AWS.config.update({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });
      
      // Send email notification
      const ses = new AWS.SES();
      const emailParams = {
        Source: 'info@relufox.ai',
        Destination: {
          ToAddresses: ['info@relufox.ai']
        },
        Message: {
          Subject: {
            Data: 'New Prescriptive AI Inquiry - Fox River AI'
          },
          Body: {
            Text: {
              Data: `A new inquiry has been submitted for prescriptive AI services:\n\nCONTACT INFORMATION:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company || 'Not provided'}\nPhone: ${contactInfo.phone || 'Not provided'}\n\nBUSINESS AREA: ${contactInfo.businessArea}\n\nUSE CASE:\n${contactInfo.useCase}\n\nPlease follow up with this customer.`
            }
          }
        }
      };
      
      await ses.sendEmail(emailParams).promise();
      
      setSubmitStatus('Thank you! Your inquiry has been submitted. We will contact you within 24 hours.');
      setContactInfo({
        name: '',
        email: '',
        company: '',
        phone: '',
        useCase: '',
        businessArea: 'pricing'
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus(`Submission failed: ${error.message || 'Unknown error'}. Please try again or contact us directly.`);
    }
  };

  return (
    <Layout>
      <div className="prescriptive-ai-page">
        <div className="hero-section">
          <h1>Prescriptive AI</h1>
          <p className="hero-subtitle">Turning Forecasts Into Action — From Insight to Impact</p>
        </div>

        <div className="content-container">
          <section className="service-overview">
            <h2>Beyond Predictions to Actions</h2>
            <p className="section-intro">Predictions are powerful — but knowing what to do next is what really drives results. Our prescriptive AI services help turn forecasts into clear, actionable strategies.</p>
            
            <div className="services-grid">
              <div className="service-item">
                <h3>Strategic Decision Support</h3>
                <p>AI-powered recommendations for complex business decisions</p>
              </div>
              <div className="service-item">
                <h3>Pricing Optimization</h3>
                <p>Dynamic pricing strategies based on market conditions and demand</p>
              </div>
              <div className="service-item">
                <h3>Resource Allocation</h3>
                <p>Optimal distribution of resources across projects and departments</p>
              </div>
              <div className="service-item">
                <h3>Customer Targeting</h3>
                <p>Identify which customer segments to focus on for maximum ROI</p>
              </div>
            </div>
          </section>

          <section className="technology-section">
            <h2>Our Approach</h2>
            <div className="tech-features">
              <div className="tech-item">
                <h4>🎯 Optimization Models</h4>
                <p>Mathematical frameworks that determine the best course of action</p>
              </div>
              <div className="tech-item">
                <h4>🤖 Agent-Based Decision Frameworks</h4>
                <p>Simulations that model complex interactions and outcomes</p>
              </div>
              <div className="tech-item">
                <h4>👥 Human-in-the-Loop Systems</h4>
                <p>Combining AI recommendations with human expertise and judgment</p>
              </div>
              <div className="tech-item">
                <h4>📊 Scenario Analysis</h4>
                <p>Evaluate multiple possible futures to prepare for different outcomes</p>
              </div>
            </div>
          </section>

          <section className="benefits-section">
            <h2>Business Benefits</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>Reduced Decision Time</h3>
                <p>Make complex decisions faster with AI-powered recommendations</p>
              </div>
              <div className="benefit-item">
                <h3>Increased ROI</h3>
                <p>Focus resources where they'll have the greatest impact</p>
              </div>
              <div className="benefit-item">
                <h3>Risk Mitigation</h3>
                <p>Identify potential issues before they become problems</p>
              </div>
              <div className="benefit-item">
                <h3>Competitive Advantage</h3>
                <p>Stay ahead with data-driven strategic decisions</p>
              </div>
            </div>
          </section>

          <section className="inquiry-section">
            <h2>Transform Your Decision-Making</h2>
            <p>Tell us about your business challenges, and we'll explore how prescriptive AI can help you make better strategic decisions.</p>
            
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Full Name *" 
                  value={contactInfo.name}
                  onChange={(e) => handleContactInfoChange('name', e.target.value)}
                  className="form-input"
                  required
                />
                <input 
                  type="email" 
                  placeholder="Email Address *" 
                  value={contactInfo.email}
                  onChange={(e) => handleContactInfoChange('email', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Company (Optional)" 
                  value={contactInfo.company}
                  onChange={(e) => handleContactInfoChange('company', e.target.value)}
                  className="form-input"
                />
                <input 
                  type="tel" 
                  placeholder="Phone (Optional)" 
                  value={contactInfo.phone}
                  onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <select
                  value={contactInfo.businessArea}
                  onChange={(e) => handleContactInfoChange('businessArea', e.target.value)}
                  className="form-select"
                >
                  <option value="pricing">Pricing Strategy</option>
                  <option value="inventory">Inventory Management</option>
                  <option value="marketing">Marketing Optimization</option>
                  <option value="operations">Operations Planning</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <textarea
                placeholder="Describe your business challenge or decision-making needs *"
                value={contactInfo.useCase}
                onChange={(e) => handleContactInfoChange('useCase', e.target.value)}
                className="form-textarea"
                rows="5"
                required
              ></textarea>
              <button type="submit" className="submit-btn">
                Request Consultation
              </button>
              {submitStatus && <p className="submit-status">{submitStatus}</p>}
            </form>
            
            <div className="contact-alternative">
              <p>Prefer to discuss your needs directly?</p>
              <a href="tel:6309408837" className="contact-btn">Call (630) 940-8837</a>
              <p>or email us at <a href="mailto:info@relufox.ai">info@relufox.ai</a></p>
            </div>
          </section>

          <section className="process-section">
            <h2>Our Implementation Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Business Analysis</h3>
                <p>Understanding your goals, constraints, and decision-making processes</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Model Development</h3>
                <p>Creating optimization models tailored to your specific needs</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Decision Support Tools</h3>
                <p>Building interactive dashboards and recommendation systems</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Integration & Training</h3>
                <p>Implementing solutions and training your team to use them effectively</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default PrescriptiveAI;